import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { useInlineRecord } from "@/hooks/use-inline-record"
import type { PageProps } from "@/types/inertia"

export type TestimonialData = {
  id?: number
  quote: string
  attribution: string
  brand: string
  display_order: number
  visible: boolean
  highlighted: boolean
  // Seeded demo copy. The model refuses to publish these.
  sample?: boolean
  // Consent record — admin-only, never rendered on the public site.
  consent_obtained_on?: string | null
  consent_source?: string | null
  internal_note?: string | null
}

const TEXTAREA_CLASS =
  "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"

export function TestimonialForm({
  testimonial,
  actionUrl,
  method,
}: {
  testimonial: TestimonialData
  actionUrl: string
  method: "post" | "patch"
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isNew = method === "post"

  const fields = useInlineRecord({
    record: {
      brand: testimonial.brand || "gisela",
      quote: testimonial.quote,
      attribution: testimonial.attribution,
      display_order: String(testimonial.display_order),
      visible: testimonial.visible,
      highlighted: testimonial.highlighted,
      consent_obtained_on: testimonial.consent_obtained_on ?? "",
      consent_source: testimonial.consent_source ?? "",
      internal_note: testimonial.internal_note ?? "",
    },
    url: actionUrl,
    param: "testimonial",
    enabled: !isNew,
  })
  const v = fields.values

  const [processing, setProcessing] = React.useState(false)
  const isSample = testimonial.sample ?? false

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      {
        testimonial: {
          ...v,
          display_order: Number(v.display_order),
          consent_obtained_on: v.consent_obtained_on || null,
          consent_source: v.consent_source || null,
          internal_note: v.internal_note || null,
        },
      },
      { onFinish: () => setProcessing(false) },
    )
  }

  const brandState = fields.state("brand")
  const quoteState = fields.state("quote")
  const attributionState = fields.state("attribution")
  const orderState = fields.state("display_order", { serialize: (value) => Number(value) })
  const visibleState = fields.state("visible")
  const highlightedState = fields.state("highlighted")
  const consentDateState = fields.state("consent_obtained_on", { serialize: (value) => value || null })
  const consentSourceState = fields.state("consent_source", { serialize: (value) => value || null })
  const noteState = fields.state("internal_note", { serialize: (value) => value || null })

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isSample && (
        <div className="rounded-lg border border-signal bg-signal-faded p-4">
          <p className="text-sm text-ink-body">
            <strong>Sample copy — not a real client.</strong> This quote was written to show the
            design with content in it. It can't be published, and should be deleted once real
            testimonials with consent on file replace it.
          </p>
        </div>
      )}

      <InlineField label="Shows on" htmlFor="brand" error={errors.brand} state={brandState}>
        <Select id="brand" value={v.brand} onChange={(e) => fields.set("brand", e.target.value)}>
          <option value="gisela">Gisela's website (therapy)</option>
          <option value="sanctuary">Sanctuary website (room hire)</option>
        </Select>
      </InlineField>

      <InlineField label="Quote" htmlFor="quote" error={errors.quote} state={quoteState}>
        <textarea
          id="quote"
          value={v.quote}
          onChange={(e) => fields.set("quote", e.target.value)}
          onKeyDown={quoteState.multilineKeys}
          required
          rows={4}
          aria-invalid={!!errors.quote}
          className={TEXTAREA_CLASS}
          placeholder="The client's words, in their voice…"
        />
      </InlineField>

      <InlineField
        label="Attribution"
        htmlFor="attribution"
        error={errors.attribution}
        state={attributionState}
      >
        <Input
          id="attribution"
          value={v.attribution}
          onChange={(e) => fields.set("attribution", e.target.value)}
          onKeyDown={attributionState.keys}
          required
          placeholder="e.g. S.M. · Anxiety client, Prestwich"
          aria-invalid={!!errors.attribution}
        />
      </InlineField>

      <div className="grid gap-4 sm:grid-cols-2">
        <InlineField
          label="Display order"
          htmlFor="display_order"
          hint="Lower numbers appear first."
          state={orderState}
        >
          <Input
            id="display_order"
            type="number"
            value={v.display_order}
            onChange={(e) => fields.set("display_order", e.target.value)}
            onKeyDown={orderState.keys}
            min={0}
          />
        </InlineField>

        <InlineField
          label="Visibility"
          htmlFor="visible"
          hint="Only publish a quote whose consent is recorded below."
          error={errors.visible}
          state={visibleState}
        >
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="visible"
              checked={v.visible}
              onChange={() => fields.set("visible", !v.visible)}
            />
            <label htmlFor="visible" className="font-normal text-ink-body">
              Show on the public site
            </label>
          </div>
        </InlineField>

        <InlineField
          label="About Me page"
          htmlFor="highlighted"
          hint="The first two featured quotes appear inline on About Me. The rest still show in the full list on the home page."
          className="sm:col-span-2"
          state={highlightedState}
        >
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="highlighted"
              checked={v.highlighted}
              onChange={() => fields.set("highlighted", !v.highlighted)}
            />
            <label htmlFor="highlighted" className="font-normal text-ink-body">
              Feature as a pull-quote on About Me
            </label>
          </div>
        </InlineField>
      </div>

      <div className="space-y-4 border-t border-hairline pt-6">
        <div>
          <h3>Consent record</h3>
          <p className="mt-1 text-sm text-ink-muted">
            Never shown on the website. Publishing a testimonial publishes that someone had
            therapy, so keep the evidence here: who agreed, when, and where the agreement is
            (email, signed form). Initials or "Anonymous" on the page doesn't remove the need
            for it, and consent can be withdrawn — delete the quote if it is.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField
            label="Consent obtained on"
            htmlFor="consent_obtained_on"
            state={consentDateState}
          >
            <Input
              id="consent_obtained_on"
              type="date"
              value={v.consent_obtained_on}
              onChange={(e) => fields.set("consent_obtained_on", e.target.value)}
              onKeyDown={consentDateState.keys}
            />
          </InlineField>

          <InlineField label="Consent source" htmlFor="consent_source" state={consentSourceState}>
            <Input
              id="consent_source"
              value={v.consent_source}
              onChange={(e) => fields.set("consent_source", e.target.value)}
              onKeyDown={consentSourceState.keys}
              placeholder="e.g. Email reply, 12 Mar 2026"
            />
          </InlineField>
        </div>

        <InlineField label="Internal note" htmlFor="internal_note" state={noteState}>
          <textarea
            id="internal_note"
            value={v.internal_note}
            onChange={(e) => fields.set("internal_note", e.target.value)}
            onKeyDown={noteState.multilineKeys}
            rows={3}
            className={TEXTAREA_CLASS}
            placeholder="Who this is, and anything worth remembering about the permission."
          />
        </InlineField>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              Add testimonial
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.history.back()}>
              Cancel
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink-muted">
            Each change is kept when you tick it — there's nothing else to save.
          </p>
        )}
      </div>
    </form>
  )
}
